/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import {
  Component, ChangeDetectionStrategy, AfterViewInit, OnDestroy, OnInit, Input,
  EventEmitter, Output, ViewChild, ViewChildren, ChangeDetectorRef, ElementRef, QueryList
} from '@angular/core';

import { isNullOrUndefined } from '@app/util/util';
import {
  DataPickerAlign, DataPickerChangedEvent, DataPickerListOption, DataPickerModel
} from '@design-system/components/organisms/data-picker/entities/data-picker.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { ImageSize } from '@design-system/components/molecules/image/entities/image.types';
import { IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { ImageDsComponent } from '@design-system/components/molecules/image/image.component';

@Component({
  selector: 'app-ds-data-picker',
  templateUrl: './data-picker.component.html',
  styleUrls: ['./data-picker.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TypographyDsComponent,
    IconDsComponent,
    ImageDsComponent,
  ]
})
export class DataPickerDsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() data: DataPickerModel[] = [];

  @Output() changedEvent: EventEmitter<DataPickerChangedEvent> = new EventEmitter<DataPickerChangedEvent>();

  @ViewChildren('pickerGroupLayer') pickerGroupLayer: QueryList<ElementRef>;
  @ViewChild('pickerHandleLayer') pickerHandleLayer: ElementRef<HTMLInputElement>;

  public readonly fontSize = TypographyFontSize.XS;
  public readonly fontFamily = TypographyFontFamily.SemiBold;
  public readonly typographyColor = TypographyColor.Primary;

  public readonly imageSize = ImageSize.MD;

  public readonly iconFillColor = IconFillColor.Primary;

  private currentIndexList: number[];
  private lastCurrentIndexList: number[];
  private groupsRectList: any[];
  private safeDoTimeoutId = null;
  private currentOption = null;

  private readonly touchOrMouse = { isTouchable: 'ontouchstart' in window, isMouseDown: false };
  private readonly draggingInfo = { isDragging: false, groupIndex: null, startPageY: null };
  private readonly itemPerDegree = 23;

  constructor(
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.currentIndexList = this.getInitialCurrentIndexList;
    this.lastCurrentIndexList = [].concat(this.currentIndexList);
    this.groupsRectList = new Array(this.data.length);
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.eventsRegister();
    this.getGroupsRectList();
    this.safeGetRectsBindEvents();
    window?.addEventListener('resize', this.safeGetRectsBindEvents.bind(this));
  }

  ngOnDestroy(): void {
    window?.removeEventListener('resize', this.safeGetRectsBindEvents.bind(this));
  }

  private get getInitialCurrentIndexList(): number[] {
    return this.data.map((item) => {
      const iCI = item.CurrentIndex;
      if (!isNullOrUndefined(iCI) && iCI >= 0 && item.List && item.List.length && iCI <= item.List.length - 1) {
        return Math.round(iCI);
      }
      return 0;
    });
  }

  public isCurrentItem(gIndex: number, iIndex: number): boolean {
    return this.currentIndexList[gIndex] === iIndex;
  }

  public getGroupAlign(model: DataPickerModel): DataPickerAlign {
    return model?.Align ?? 'center';
  }

  public getItemStyle(gIndex: number, iIndex: number): { [key: string]: any } {
    const gapCount = this.currentIndexList[gIndex] - iIndex;
    if (Math.abs(gapCount) < 90 / this.itemPerDegree) {
      const rotateStyle = {
        transform: `rotateX(${gapCount * this.itemPerDegree}deg) translate3d(0, 0, 10.625em)`,
        opacity: (1 - Math.abs(gapCount) / (90 / this.itemPerDegree)).toString(),
      };
      if (!this.draggingInfo.isDragging) {
        rotateStyle['transition'] = 'transform 150ms ease-out';
      }
      return rotateStyle;
    }
    return { transform: `rotateX(${gapCount > 0 ? 90 : -90}deg) translate3d(0, 0, 10.625em)` };
  }

  public trackByGroup(index: number): string {
    return `group-${index}`;
  }

  public trackByItem(index: number, item: DataPickerListOption): string {
    return `item-${index}-${item.Id}`;
  }

  private setCurrentOption(gIndex: number, iIndex: number): void {
    this.currentOption = this.data?.[gIndex]?.List?.[iIndex];
    this.cdr.detectChanges();
  }

  private safeGetRectsBindEvents(): void {
    if (this.safeDoTimeoutId) {
      clearTimeout(this.safeDoTimeoutId);
    }
    this.safeDoTimeoutId = setTimeout(() => {
      this.getGroupsRectList();
    }, 200);
  }

  private getGroupsRectList(): void {
    if (this.pickerGroupLayer) {
      this.pickerGroupLayer.toArray().forEach((item, index) => {
        this.groupsRectList[index] = item?.nativeElement?.getBoundingClientRect();
      })
    }
  }

  private eventsRegister(): void {
    if (this.pickerHandleLayer?.nativeElement) {
      this.addEventsForElement(this.pickerHandleLayer.nativeElement);
    }
  }

  private addEventsForElement(el: HTMLElement): void {
    const eventHandlerList = [
      { name: this.touchOrMouse.isTouchable ? 'touchstart' : 'mousedown', handler: this.handleStart },
      { name: this.touchOrMouse.isTouchable ? 'touchmove' : 'mousemove', handler: this.handleMove },
      { name: this.touchOrMouse.isTouchable ? 'touchend' : 'mouseup', handler: this.handleEnd },
      { name: this.touchOrMouse.isTouchable ? 'touchcancel' : 'mouseleave', handler: this.handleCancel }
    ];
    eventHandlerList.forEach((item) => {
      el.removeEventListener(item.name, item.handler, false);
      el.addEventListener(item.name, item.handler.bind(this), false);
    })
  }

  private triggerAboveLayerClick(gIndex: number): void {
    const movedIndex = this.currentIndexList[gIndex] + 1;
    this.currentIndexList[gIndex] = movedIndex;
    this.cdr.detectChanges();
    this.correctionCurrentIndex(gIndex);
  }

  private triggerBelowLayerClick(gIndex: number): void {
    const movedIndex = this.currentIndexList[gIndex] - 1;
    this.currentIndexList[gIndex] = movedIndex;
    this.cdr.detectChanges();
    this.correctionCurrentIndex(gIndex);
  }

  private getTouchInfo(ev: any): any {
    return this.touchOrMouse.isTouchable ? ev?.changedTouches?.[0] || ev?.touches?.[0] : ev;
  }

  private getGroupIndexBelongsEvent(ev: Event): number {
    const touchInfo = this.getTouchInfo(ev);
    for (let i = 0; i < this.groupsRectList.length; i++) {
      const item = this.groupsRectList[i];
      if (item.left < touchInfo.pageX && touchInfo.pageX < item.right) {
        return i;
      }
    }
    return null;
  }

  private handleEventClick(ev: any): void {
    const gIndex = this.getGroupIndexBelongsEvent(ev);
    switch (ev.target.dataset.type) {
      case 'top':
        this.triggerAboveLayerClick(gIndex);
        break;
      case 'bottom':
        this.triggerBelowLayerClick(gIndex);
        break;
    }
    this.cdr.detectChanges();
  }

  private handleStart(ev: Event): void {
    if (ev.cancelable) {
      ev?.preventDefault?.();
      ev?.stopPropagation?.();
    }
    const touchInfo = this.getTouchInfo(ev);
    this.draggingInfo.startPageY = touchInfo.pageY;
    if (!this.touchOrMouse.isTouchable) {
      this.touchOrMouse.isMouseDown = true;
    }
    this.cdr.detectChanges();
  }

  private handleMove(ev: Event): void {
    if (ev.cancelable) {
      ev?.preventDefault?.();
      ev?.stopPropagation?.();
    }
    if (this.touchOrMouse.isTouchable || this.touchOrMouse.isMouseDown) {
      this.draggingInfo.isDragging = true;
      this.setCurrentIndexOnMove(ev);
    }
    this.cdr.detectChanges();
  }

  private handleEnd(ev: Event): void {
    ev?.preventDefault?.();
    ev?.stopPropagation?.();
    if (!this.draggingInfo.isDragging) {
      this.handleEventClick(ev);
    }
    this.draggingInfo.isDragging = false;
    this.touchOrMouse.isMouseDown = false;
    this.correctionAfterDragging();
    this.cdr.detectChanges();
  }

  private handleCancel(ev: Event): void {
    ev?.preventDefault?.();
    ev?.stopPropagation?.();
    if (this.touchOrMouse.isTouchable || this.touchOrMouse.isMouseDown) {
      this.correctionAfterDragging();
      this.touchOrMouse.isMouseDown = false;
      this.draggingInfo.isDragging = false;
      this.cdr.detectChanges();
    }
  }

  private setCurrentIndexOnMove(ev: Event): void {
    const touchInfo = this.getTouchInfo(ev);
    if (this.draggingInfo.groupIndex === null) {
      this.draggingInfo.groupIndex = this.getGroupIndexBelongsEvent(ev);
      this.cdr.detectChanges();
    }
    const gIndex = this.draggingInfo.groupIndex;

    if (this.data?.[gIndex]?.List) {
      const moveCount = (this.draggingInfo.startPageY - touchInfo.pageY) / 32;
      const movedIndex = this.currentIndexList[gIndex] + moveCount;
      this.currentIndexList[gIndex] = movedIndex;
      this.draggingInfo.startPageY = touchInfo.pageY;
      this.cdr.detectChanges();
    }
  }

  private correctionAfterDragging(): void {
    const gIndex = this.draggingInfo.groupIndex;
    this.correctionCurrentIndex(gIndex);
    this.draggingInfo.groupIndex = null;
    this.draggingInfo.startPageY = null;
    this.cdr.detectChanges();
  }

  private correctionCurrentIndex(gIndex: number): void {
    if (this.data?.[gIndex]?.List?.length > 0) {
      const unsafeGroupIndex = this.currentIndexList[gIndex];

      let movedIndex = unsafeGroupIndex;
      if (unsafeGroupIndex > this.data[gIndex].List.length - 1) {
        movedIndex = this.data[gIndex].List.length - 1;
      } else if (unsafeGroupIndex < 0) {
        movedIndex = 0;
      }
      movedIndex = Math.round(movedIndex);
      this.currentIndexList[gIndex] = movedIndex;
      if (movedIndex !== this.lastCurrentIndexList[gIndex]) {
        this.setCurrentOption(gIndex, movedIndex);
        this.changedEvent.emit({ gIndex, selectedIndex: movedIndex, option: this.currentOption });
      }
      this.lastCurrentIndexList = [].concat(this.currentIndexList);
      this.cdr.detectChanges();
    }
  }

}
